import { useState } from "react";

import { IAiPromptTemplateListItem } from "../../../@types/admin/AdminAiPromptTemplate";
import {
  useAddAiPromptTemplate,
  useUpdateAiPromptTemplate,
} from "../../../hooks/mutations/admin/aiPromptTemplates";
import { useAiPromptTemplate } from "../../../hooks/queries/admin/aiPromptTemplates";
import { useAllWardOptions } from "../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  ErrorMessage,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  LongTextEditor,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../shared";

/**
 * Retool's `promptKey` select was a static two-option list, not a catalogue
 * endpoint. Kept as-is: the key identifies which code path consumes the
 * prompt, so it isn't free text.
 */
const PROMPT_KEYS = [
  { value: "note_summary", label: "Note insights" },
  { value: "gvhd_risk_assessment", label: "GVHD Risk Assessment" },
];

/** `AddAiPromptTemplateRequestDto` caps the name at 200. */
const MAX_NAME_LENGTH = 200;

interface IAiPromptTemplateModalProps {
  /** Omitted for a create. */
  template?: IAiPromptTemplateListItem;
  onClose: () => void;
}

/**
 * Create/edit an AI prompt template — Retool's `modalAddPromptTemplate` and
 * `modaEditPromptTemplate` (EN-5161), which were two near-identical forms.
 *
 * The body is edited in the shared `LongTextEditor`, the same control the
 * email templates page uses, so a prompt round-trips byte for byte.
 */
export function AiPromptTemplateModal({
  template,
  onClose,
}: IAiPromptTemplateModalProps) {
  const isEdit = Boolean(template);
  const { showToast } = useToast();
  const { wards, isLoading: wardsLoading } = useAllWardOptions();
  // The list row has everything but the body, so an edit loads the full record.
  const { aiPromptTemplate, isLoading: templateLoading } = useAiPromptTemplate(
    template?.id
  );
  const { addAiPromptTemplate, isAddingTemplate } = useAddAiPromptTemplate();
  const { updateAiPromptTemplate, isUpdatingTemplate } =
    useUpdateAiPromptTemplate();

  const [promptKey, setPromptKey] = useState(template?.promptKey ?? "");
  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [wardId, setWardId] = useState(
    template?.wardId != null ? String(template.wardId) : ""
  );
  const [body, setBody] = useState("");
  // The body arrives after the modal opens; seed it once, then leave the
  // operator's edits alone.
  const [isBodySeeded, setIsBodySeeded] = useState(!isEdit);

  if (isEdit && !isBodySeeded && aiPromptTemplate) {
    setBody(aiPromptTemplate.template);
    setIsBodySeeded(true);
  }

  const isSaving = isAddingTemplate || isUpdatingTemplate;
  const isLoadingBody = isEdit && templateLoading;

  const errors = {
    promptKey: promptKey ? undefined : "Select a prompt key.",
    name: name.trim() ? undefined : "Name is required.",
    body: body.trim() ? undefined : "The template body is required.",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const shownError = (field: keyof typeof errors) =>
    isEdit || touched[field] ? errors[field] : undefined;
  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSave = async () => {
    if (hasErrors) return;

    const payload = {
      promptKey,
      name: name.trim(),
      description: description.trim() || null,
      template: body,
      wardId: wardId ? Number(wardId) : null,
      // Retool hard-coded isActive: true and version: 1 on BOTH create and
      // update. On update that silently rewrites the stored version, and
      // (PromptKey, WardId, Version) is a unique index — so an edit could
      // collide with an existing row. The loaded record's values are sent
      // back instead; only a create defaults to version 1.
      isActive: aiPromptTemplate?.isActive ?? true,
      version: aiPromptTemplate?.version ?? 1,
      modelName: aiPromptTemplate?.modelName ?? null,
      temperature: aiPromptTemplate?.temperature ?? null,
      maxTokens: aiPromptTemplate?.maxTokens ?? null,
    };

    try {
      if (template) {
        await updateAiPromptTemplate({ ...payload, id: template.id });
        showToast("The template was updated successfully.", "success");
      } else {
        await addAiPromptTemplate(payload);
        showToast("The template was created successfully.", "success");
      }
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          isEdit
            ? "There was an error updating this template."
            : "There was an error creating this template."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{isEdit ? template?.name : "Add prompt template"}</ModalTitle>

      <ModalForm>
        {isEdit && promptKey === "note_summary" && (
          <WarningNotice>
            This prompt drives the patient note insights shown to clinicians.
            Changing its wording changes what the model produces — review the
            result before leaving it in place.
          </WarningNotice>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="prompt-key">Prompt key</FilterLabel>
          <AdminSelectField
            id="prompt-key"
            value={promptKey}
            // The key selects which code path consumes the prompt, so an
            // existing template's key is not editable.
            disabled={isEdit || isSaving}
            onBlur={() => touch("promptKey")}
            onChange={(e) => {
              touch("promptKey");
              setPromptKey(e.target.value);
            }}
          >
            <option value="">Select an option</option>
            {PROMPT_KEYS.map((key) => (
              <option key={key.value} value={key.value}>
                {key.label}
              </option>
            ))}
          </AdminSelectField>
          {shownError("promptKey") && (
            <FieldError>{shownError("promptKey")}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="prompt-ward">Ward</FilterLabel>
          <AdminSelectField
            id="prompt-ward"
            value={wardId}
            disabled={wardsLoading || isSaving}
            onChange={(e) => setWardId(e.target.value)}
          >
            <option value="">
              {wardsLoading ? "Loading..." : "All wards (no override)"}
            </option>
            {wards.map((ward) => (
              <option key={ward.id} value={ward.id}>
                {ward.id} - {ward.name}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            A ward-specific template overrides the general one for that ward.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="prompt-name">Name</FilterLabel>
          <FilterInput
            id="prompt-name"
            type="text"
            maxLength={MAX_NAME_LENGTH}
            value={name}
            disabled={isSaving}
            onBlur={() => touch("name")}
            onChange={(e) => {
              touch("name");
              setName(e.target.value);
            }}
          />
          {shownError("name") && <FieldError>{shownError("name")}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="prompt-description">Description</FilterLabel>
          <FilterInput
            id="prompt-description"
            type="text"
            value={description}
            disabled={isSaving}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FilterGroup>

        {isLoadingBody ? (
          <ErrorMessage>Loading the template body...</ErrorMessage>
        ) : (
          <FilterGroup>
            <LongTextEditor
              id="prompt-template"
              label="Template"
              value={body}
              wrap
              disabled={isSaving}
              hint="Stored exactly as typed — leading spaces, blank lines and line breaks are all preserved."
              onChange={(next) => {
                touch("body");
                setBody(next);
              }}
            />
            {shownError("body") && (
              <FieldError>{shownError("body")}</FieldError>
            )}
          </FilterGroup>
        )}

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving || hasErrors || isLoadingBody}
            onClick={handleSave}
          >
            {(() => {
              if (isSaving) return isEdit ? "Saving..." : "Creating...";
              return isEdit ? "Save" : "Create";
            })()}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
